import { useMemo } from "react";
import type {
  ActionDescription,
  ActionOutputData,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import { useRootStore } from "../../../../../../stores/hooks";
import { translateAction } from "../../../../../../locales/i18n";
import { getErrorMessage } from "../../../../ActivityGrid/ActivityGrid.helpers";
import type { ActionCandidate } from "../../../../../../types/candidate";

type ActionStatus = "is_success" | "is_error";

interface ActionDetailsResult {
  selectedIntegration?: IntegrationDescription;
  selectedAction?: ActionDescription;
  selectedActionFallback: ActionCandidate | undefined;
  status: ActionStatus;
  errorMessage: string;
  actionLabel: string;
}

/**
 * Custom hook that extracts common logic for working with action details
 * Centralizes the logic for finding integration, action, status and error message
 */
export const useActionDetails = (
  action: ActionOutputData,
): ActionDetailsResult => {
  const { integrations, jobDetails, automations } = useRootStore();

  // Find the integration that contains this action
  const selectedIntegration = useMemo(
    () =>
      integrations.list.find(
        (i) => i.integrationId === action.actionReference?.integrationId,
      ),
    [integrations.list, action.actionReference?.integrationId],
  );

  // Find the specific action within the integration
  const selectedAction = useMemo(
    () =>
      selectedIntegration?.actions.find(
        (a) => a.actionId === action.actionReference?.actionId,
      ),
    [selectedIntegration?.actions, action.actionReference?.actionId],
  );

  // Determine action status
  const status = useMemo(
    (): ActionStatus =>
      action.status.oneofKind === "ok" ? "is_success" : "is_error",
    [action.status.oneofKind],
  );

  // Get error message if there is one
  const errorMessage = useMemo(
    () => getErrorMessage(action, jobDetails.job),
    [action, jobDetails.job],
  );

  // Get translated action label
  const actionLabel = useMemo(
    () => (selectedAction ? translateAction(selectedAction) : ""),
    [selectedAction],
  );

  const selectedActionFallback = useMemo(
    () =>
      automations.candidate.allActions.find(
        (actionInput: ActionCandidate) =>
          actionInput.actionReference?.instanceId ===
          action.actionReference?.instanceId,
      ),
    [automations.candidate.allActions, action.actionReference?.instanceId],
  );

  return {
    selectedIntegration,
    selectedAction,
    status,
    errorMessage,
    actionLabel,
    selectedActionFallback,
  };
};
